import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowSquareOut, Browser, HandHeart, Path, Storefront } from "@phosphor-icons/react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { publicLink } from "@/lib/landerDomain";

const TONE = {
  ok: "border-emerald-700 bg-emerald-50 text-emerald-900",
  warn: "border-amber-600 bg-amber-50 text-amber-900",
  fail: "border-red-600 bg-red-50 text-red-900",
  info: "border-zinc-500 bg-zinc-50 text-zinc-800",
};
const STATUS = {
  delivered: ["Got it", "bg-emerald-700 text-white"],
  retrying: ["Retrying", "bg-amber-100 border border-amber-700 text-amber-900"],
  scheduled: ["Waiting for hours", "bg-amber-100 border border-amber-700 text-amber-900"],
  failed_final: ["Failed", "bg-red-600 text-white"],
  abandoned: ["Failed", "bg-red-600 text-white"],
};

function Cell({ icon: Icon, label, testId, children }) {
  return (
    <div className="border-2 border-black bg-white p-3 min-w-0" data-testid={testId}>
      <div className="label-mono flex items-center gap-1 mb-1"><Icon size={12} weight="bold" /> {label}</div>
      <div className="text-sm break-words">{children}</div>
    </div>
  );
}

function Tries({ tries }) {
  if (!tries.length) return <span className="text-zinc-500">No buyer was tried.</span>;
  return (
    <ul className="space-y-1.5">
      {tries.map((t, i) => {
        const [label, cls] = STATUS[t.status] || [t.status, "bg-zinc-200"];
        return (
          <li key={i} className="text-xs" data-testid={`route-summary-try-${i}`}>
            <span className={`mr-1 px-1 text-[9px] uppercase tracking-widest ${cls}`}>{label}</span>
            <b>{t.company || t.destination}</b>{t.company && t.destination !== t.company ? ` · ${t.destination}` : ""}
            {t.extra_place && <span className="text-zinc-500"> (extra place)</span>}
            <span className="text-zinc-500"> · {fmtDate(t.at)}</span>
            {t.reason && <div className="text-red-700">{t.reason}</div>}
            {t.status === "retrying" && t.next_retry_at && <div className="text-zinc-600">Next try {fmtDate(t.next_retry_at)}</div>}
            {t.status === "scheduled" && t.next_retry_at && <div className="text-zinc-600">Sends when they open: {fmtDate(t.next_retry_at)}</div>}
          </li>
        );
      })}
    </ul>
  );
}

function ThankYouShown({ ty }) {
  if (!ty) return <span className="text-zinc-500">No thank-you page recorded.</span>;
  return (
    <div className="space-y-0.5 text-xs">
      <div className="text-sm"><b data-testid="route-summary-ty-firm">{ty.firm || "No firm named (our company page)"}</b></div>
      {ty.headline && <div data-testid="route-summary-ty-headline">&ldquo;{ty.headline}&rdquo;</div>}
      <div>Phone shown: {ty.phone || "none"}</div>
      <div className="text-zinc-500">{ty.seen ? `Seen by the visitor ${fmtDate(ty.at)}` : "Planned: the visitor's view wasn't recorded"}</div>
      {ty.view_path && (
        <a href={publicLink(ty.view_path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline"
           data-testid="route-summary-ty-view">View it <ArrowSquareOut size={11} /></a>
      )}
    </div>
  );
}

// Lead Detail summary box: result, buyer, every buyer tried, landing page and thank-you shown.
export function LeadRouteSummary({ lead }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/leads/${lead.id}/route-summary`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [lead]);
  if (!d) return null;
  const lp = d.landing_page;
  return (
    <div className="brut-card p-4 mb-4 space-y-3" data-testid="route-summary">
      <div className={`border-2 px-3 py-2 text-sm ${TONE[d.result.tone] || TONE.info}`} data-testid="route-summary-result">
        <b>Where it went:</b> {d.result.text}{" "}
        <Link to={`/leads/${lead.id}?tab=delivery`} className="underline" data-testid="route-summary-delivery-link">Delivery tab</Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <Cell icon={HandHeart} label="Buyer it went to" testId="route-summary-buyer">
          {d.buyer ? <><b>{d.buyer.company || d.buyer.destination}</b><div className="text-xs text-zinc-500">{d.buyer.destination} · {fmtDate(d.buyer.at)}</div></>
            : <span className="text-red-700">No buyer has it yet</span>}
        </Cell>
        <Cell icon={Path} label="Buyers tried" testId="route-summary-tries"><Tries tries={d.tries} /></Cell>
        <Cell icon={Storefront} label="Landing page" testId="route-summary-landing">
          {lp ? <>
            <b>{lp.name || lp.slug}</b>{lp.firm ? <div className="text-xs">{lp.firm}</div> : null}
            <div className="flex gap-3 text-xs mt-1">
              <a href={publicLink(lp.public_path)} target="_blank" rel="noreferrer" className="underline" data-testid="route-summary-landing-view">View</a>
              {lp.editor_href && <Link to={lp.editor_href} className="underline" data-testid="route-summary-landing-edit">Edit</Link>}
            </div>
          </> : <span className="text-zinc-500">Not from a landing page</span>}
        </Cell>
        <Cell icon={Browser} label="Thank-you page shown" testId="route-summary-thankyou"><ThankYouShown ty={d.thankyou} /></Cell>
      </div>
      {d.notes.length > 0 && (
        <ul className="text-xs text-zinc-700 space-y-0.5" data-testid="route-summary-notes">
          {d.notes.map((n, i) => <li key={i}>{fmtDate(n.at)} · {n.text}</li>)}
        </ul>
      )}
    </div>
  );
}
